import { SimpleGrid, Stack } from "@mantine/core";
import { educationLevelLabel } from "../../lib/educationCatalog";
import {
  ENGLISH_LEVEL_REQUIRED_LABELS,
  EXPERIENCE_TYPE_LABELS,
  PAY_TYPE_LABELS,
  WHATSAPP_ALERT_LABELS,
  WORK_MODE_LABELS,
  workModeFromJob,
} from "../../lib/formCatalogs";
import { formatJobExperienceRange, formatPaise } from "../../lib/format";
import { perkLabels } from "../../lib/perksCatalog";
import { axioTokens } from "../../theme";
import type { RequiredSkill } from "../../types/api";
import { JobFactCard, JOB_FACT_GRID_COLS } from "./JobFactCard";
import { JobRequiredSkills } from "./JobRequiredSkills";

type Audience = "hr" | "recruiter";
type JobFact = { label: string; value: string };

export type JobAdditionalDetailsJob = {
  education_level?: string | null;
  perk_ids?: string[];
  work_mode?: string | null;
  is_remote?: boolean;
  pay_type?: "fixed" | "fixed_incentive" | "incentive_only" | null;
  incentive_min_paise?: number | null;
  incentive_max_paise?: number | null;
  experience_min?: number | null;
  experience_max?: number | null;
  experience_type?: "any" | "fresher" | "experienced" | null;
  english_level_required?: "none" | "basic" | "good" | null;
  required_languages?: string[];
  max_notice_days?: number | null;
  is_walkin?: boolean;
  walkin_address?: string | null;
  requires_joining_fee?: boolean;
  contact_preference?: "candidates_call_recruiter" | "candidates_call_other" | "recruiter_contacts_first" | null;
  whatsapp_alert?: "daily_digest" | "per_application_self" | "per_application_other" | null;
  required_skills?: RequiredSkill[];
};

const JOB_DETAILS_COPY = {
  workSetup: "Work setup",
  payType: "Pay type",
  incentive: "Incentive / month",
  benefits: "Benefits",
  experience: "Experience",
  education: "Education",
  english: "English requirement",
  languages: "Languages required",
  noticePeriod: "Notice period",
  walkIn: "Walk-in",
  joiningFee: "Joining fee",
  whatsappAlerts: "WhatsApp alerts",
  immediateJoiner: "Immediate joiner",
  noticePeriodSuffix: "days maximum",
  walkInAvailable: "Walk-in available",
  joiningFeeRequired: "Required",
  joiningFeeNotRequired: "Not required",
} as const;

function labelFor<T extends string>(labels: Partial<Record<T, string>>, value: string | null | undefined): string | null {
  return value ? labels[value as T] ?? null : null;
}

function noticePeriodLabel(days: number): string {
  return days === 0 ? JOB_DETAILS_COPY.immediateJoiner : `${days} ${JOB_DETAILS_COPY.noticePeriodSuffix}`;
}

function experienceLabel(job: JobAdditionalDetailsJob): string | null {
  const range = formatJobExperienceRange(job.experience_min, job.experience_max);
  const type = labelFor(EXPERIENCE_TYPE_LABELS, job.experience_type);
  return [range, type].filter(Boolean).join(" · ") || null;
}

function incentiveLabel(job: JobAdditionalDetailsJob): string | null {
  if (job.incentive_min_paise == null && job.incentive_max_paise == null) return null;
  return `${formatPaise(job.incentive_min_paise)} – ${formatPaise(job.incentive_max_paise)}`;
}

export function JobAdditionalDetails({
  job,
  audience,
}: {
  job: JobAdditionalDetailsJob;
  audience: Audience;
}) {
  const education = educationLevelLabel(job.education_level);
  const perks = job.perk_ids?.length ? perkLabels(job.perk_ids).join(", ") : null;
  const workMode = WORK_MODE_LABELS[workModeFromJob(job.work_mode, job.is_remote)];
  const facts: Array<JobFact | null> = [
    { label: JOB_DETAILS_COPY.workSetup, value: workMode },
    job.pay_type ? { label: JOB_DETAILS_COPY.payType, value: PAY_TYPE_LABELS[job.pay_type] } : null,
    incentiveLabel(job) ? { label: JOB_DETAILS_COPY.incentive, value: incentiveLabel(job)! } : null,
    perks ? { label: JOB_DETAILS_COPY.benefits, value: perks } : null,
    experienceLabel(job) ? { label: JOB_DETAILS_COPY.experience, value: experienceLabel(job)! } : null,
    education ? { label: JOB_DETAILS_COPY.education, value: education } : null,
    job.english_level_required
      ? { label: JOB_DETAILS_COPY.english, value: ENGLISH_LEVEL_REQUIRED_LABELS[job.english_level_required] }
      : null,
    job.required_languages?.length
      ? { label: JOB_DETAILS_COPY.languages, value: job.required_languages.join(", ") }
      : null,
    job.max_notice_days != null
      ? { label: JOB_DETAILS_COPY.noticePeriod, value: noticePeriodLabel(job.max_notice_days) }
      : null,
    job.is_walkin
      ? { label: JOB_DETAILS_COPY.walkIn, value: job.walkin_address || JOB_DETAILS_COPY.walkInAvailable }
      : null,
    {
      label: JOB_DETAILS_COPY.joiningFee,
      value: job.requires_joining_fee ? JOB_DETAILS_COPY.joiningFeeRequired : JOB_DETAILS_COPY.joiningFeeNotRequired,
    },
    audience === "hr" && labelFor(WHATSAPP_ALERT_LABELS, job.whatsapp_alert)
      ? { label: JOB_DETAILS_COPY.whatsappAlerts, value: labelFor(WHATSAPP_ALERT_LABELS, job.whatsapp_alert)! }
      : null,
  ];
  const visibleFacts = facts.filter((fact): fact is JobFact => fact !== null);

  return (
    <Stack gap={axioTokens.unit[32]}>
      {visibleFacts.length > 0 ? (
        <SimpleGrid type="container" cols={JOB_FACT_GRID_COLS} spacing="lg">
          {visibleFacts.map((fact) => (
            <JobFactCard key={fact.label} label={fact.label}>
              {fact.value}
            </JobFactCard>
          ))}
        </SimpleGrid>
      ) : null}
      <JobRequiredSkills skills={job.required_skills} />
    </Stack>
  );
}
